import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ConfigService } from '@bo-client/config/config.service';
import { GisDataModel } from '@bo-client/shared/models/gisData';
import { AreaTypeEnum } from '@bo-client/activation-management/shared/models/commissioningModel';
import { map } from 'rxjs/operators';
import { AutocompleteStrategy } from '@bo-client/shared/components/get-gis-data/autocomplete.strategy';
import { MapAreasTHENEnum } from '@bo-client/activation-management/shared/components/select-gis-data/select-gis-data.model';

export class THNAutocompleteStrategy implements AutocompleteStrategy {
  constructor(public httpClientBack: HttpClient, public configService: ConfigService) {}

  getCitySuggestions(val: string): Observable<GisDataModel[]> {
    return this.httpApiTHN(
      'Vorschlagsliste_PLZ_Ort',
      JSON.stringify({
        plz_ort: val.toString()
      })
    ).pipe(map((x) => this.listCityToShow(x)));
  }

  getStreetSuggestions(selectedGisData: GisDataModel, val: string): Observable<GisDataModel[]> {
    return this.httpApiTHN(
      'Vorschlagsliste_Strasse',
      JSON.stringify({
        ID_ort: selectedGisData.cityId,
        strasse: val.toString()
      })
    ).pipe(map((x) => this.listStreetToShow(x)));
  }

  getStreetNoSuggestions(selectedGisData: GisDataModel, val: string): Observable<GisDataModel[]> {
    return this.httpApiTHN(
      'Vorschlagsliste_Hausnummer',
      JSON.stringify({
        ID_strasse: selectedGisData.streetId,
        hausnummer: val.toString()
      })
    ).pipe(map((x) => this.listStreetNoToShow(x)));
  }

  listCityToShow(apiRes: any): GisDataModel[] {
    return apiRes.d.map((ele) => ({
      cityId: ele.id,
      city: ele.value.ort,
      zipCode: ele.value.plz,
      orgEinheit: ele.value.then_org_einheit
    }));
  }

  listStreetToShow(apiRes: any): GisDataModel[] {
    return apiRes.d.map((ele) => ({
      id: ele.id,
      streetId: ele.id,
      street: ele.value.strasse,
      sapDistrictNumber: ele.value.sap_ort,
      originalData: ele.value
    }));
  }

  listStreetNoToShow(apiRes: any): GisDataModel[] {
    return apiRes.d.map((ele) => ({
      id: ele.id,
      houseNo: ele.value.hnrZahl,
      houseNoAddition: ele.value.hnrZusatz,
      availableAreas: ele.value.sparte.code,
      district: ele.value.ortsteil,
      sapDistrictNumber: ele.value.sapOrt,
      originalData: ele.value
    }));
  }

  /**
   * Überprüft, ob eine spezifische Area verfügbar ist.sparte
   *
   * @param areaType - Der Typ der Area, die geprüft werden soll.sps
   * @param selectedOption - Das ausgewählte GisDataModel.
   * @return boolean - Gibt true zurück, wenn die Area verfügbar ist, sonst false.
   */
  isAvailableArea(areaType: AreaTypeEnum, selectedOption: GisDataModel): boolean {
    // Sonderfall -> Prüfung ob hausanschluss bereits vorhanden ist.
    if (areaType === AreaTypeEnum.POWER && selectedOption?.originalData?.eleHa?.description === 'IN') {
      return true;
    }

    // Wenn keine verfügbaren Areas vorhanden sind, sofort false zurückgeben.
    if (!selectedOption.availableAreas) {
      return false;
    }

    // Prüft, ob der Code der ausgewählten Option in der MapAreasTHENEnum vorhanden ist.
    const isCodeInEnum = MapAreasTHENEnum.some((element) => element.code === selectedOption.availableAreas);
    if (!isCodeInEnum) {
      return false;
    }

    // Sucht das entsprechende Element in der MapAreasTHENEnum.
    const enumElement = MapAreasTHENEnum.find((element) => element.code === selectedOption.availableAreas);

    // Prüft, ob der areaType in den Areas des gefundenen Elements vorhanden ist.
    const isAreaInEnumElement = enumElement.areas.some(
      (element) => element.toLowerCase() === areaType.toLowerCase()
    );

    return isAreaInEnumElement;
  }

  private httpApiTHN(apiUri: string, data: any): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.httpClientBack.post<any>(`${this.configService.getSettings('gisURLTHN')}${apiUri}`, data, {
      headers: headers
    });
  }
}
